import { useCallback, useEffect, useState } from 'react'
import * as adminApi from './adminApi'
import { ApiError } from './api'

/**
 * Where the console is in signing in.
 *
 * `disabled` is its own state rather than an error: the server answers 404 on
 * every admin route when WEDDING_ADMIN_PASSWORD is unset, and the console
 * should say so plainly instead of showing a password box that can never work.
 */
export type AdminPhase = 'checking' | 'disabled' | 'locked' | 'unlocked'

export interface AdminSession {
  phase: AdminPhase
  error: string | null
  signingIn: boolean
  signIn: (password: string) => Promise<void>
  signOut: () => Promise<void>
  /** Called when a request comes back 401, e.g. the session expired mid-edit. */
  sessionLost: () => void
}

export function useAdminSession(): AdminSession {
  const [phase, setPhase] = useState<AdminPhase>('checking')
  const [error, setError] = useState<string | null>(null)
  const [signingIn, setSigningIn] = useState(false)

  useEffect(() => {
    let cancelled = false

    void (async () => {
      try {
        const authenticated = await adminApi.getSession()
        if (cancelled) return
        setPhase(authenticated ? 'unlocked' : 'locked')
      } catch (err) {
        if (cancelled) return
        // 404 means no admin password is configured on the server at all.
        setPhase(err instanceof ApiError && err.status === 404 ? 'disabled' : 'locked')
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  const signIn = useCallback(async (password: string) => {
    setSigningIn(true)
    setError(null)
    try {
      await adminApi.login(password)
      setPhase('unlocked')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSigningIn(false)
    }
  }, [])

  const signOut = useCallback(async () => {
    try {
      await adminApi.logout()
    } catch {
      // Asked to leave, so drop local state regardless.
    }
    setPhase('locked')
    setError(null)
  }, [])

  const sessionLost = useCallback(() => {
    setPhase('locked')
    setError('Your session expired. Please sign in again.')
  }, [])

  return { phase, error, signingIn, signIn, signOut, sessionLost }
}
